import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Pause, Play, X, RefreshCw, AlertTriangle, CheckCircle2, Clock, Loader2, Mail, Users, Calendar, Cloud, ArrowRightLeft, Plus, CheckSquare } from 'lucide-react'
import { execution, projects, migrationReport, waves as wavesApi, type ExecutionStatus, type WaveItemDetail, type MailboxProgress, type EwsLoad, type IdleMeter } from '../api/client'
import { MigrationProgressBar, AggregateProgressBar } from '@/components/MigrationProgressBar'
import { CircuitBreakerBadge } from '@/components/CircuitBreakerBadge'
import { MailboxDetailDrawer } from '@/components/MailboxDetailDrawer'

export default function ExecutionPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const qc = useQueryClient()
  const [showStartModal, setShowStartModal] = useState(false)
  const [password, setPassword] = useState('')
  const [forceChange, setForceChange] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [drawerItemId, setDrawerItemId] = useState<string | null>(null)

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projects.get(projectId!),
    enabled: !!projectId,
  })

  const { data: status } = useQuery({
    queryKey: ['execution-status', projectId],
    queryFn: () => execution.status(projectId!),
    enabled: !!projectId,
    refetchInterval: 3000,
  })

  const { data: progressData } = useQuery({
    queryKey: ['migration-progress', projectId],
    queryFn: () => migrationReport.progress(projectId!),
    enabled: !!projectId,
    refetchInterval: 5000,
  })

  const progressByItemId = new Map<string, MailboxProgress>()
  progressData?.items.forEach((it) => progressByItemId.set(it.waveItemId, it))

  const startMut = useMutation({
    mutationFn: (req: { provisionalPassword: string; forcePasswordChange: boolean }) =>
      execution.start(projectId!, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['execution-status', projectId] })
      setShowStartModal(false)
      setError(null)
    },
    onError: (e: any) => setError(e?.response?.data?.error || e?.message || 'Erro ao iniciar'),
  })
  const pauseMut = useMutation({
    mutationFn: () => execution.pause(projectId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['execution-status', projectId] }),
  })
  const resumeMut = useMutation({
    mutationFn: () => execution.resume(projectId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['execution-status', projectId] }),
  })
  const cancelMut = useMutation({
    mutationFn: () => execution.cancel(projectId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['execution-status', projectId] }),
  })
  // Sprint 9: mover usuário entre ondas (organização). Atualiza ondas + listas de itens no sucesso.
  const moveMut = useMutation({
    mutationFn: ({ waveItemId, data }: { waveItemId: string; data: { targetWaveId?: string; createNewWave?: boolean } }) =>
      wavesApi.moveItem(projectId!, waveItemId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['execution-status', projectId] })
      qc.invalidateQueries({ queryKey: ['wave-items'] })
      setError(null)
    },
    onError: (e: any) => setError(e?.response?.data?.error || e?.message || 'Erro ao mover usuário de onda'),
  })

  if (!status) return <div className="p-6">Carregando...</div>

  // 'start' (com senha provisória) é a PRIMEIRA execução — só em Approved.
  // Retomar um plano pausado é 'resume' (botão "Retomar"), não 'start'.
  const canStart = status.planStatus === 'Approved'
  const canPause = status.planStatus === 'Executing'
  const canResume = status.planStatus === 'Paused'
  const canCancel = status.planStatus === 'Executing' || status.planStatus === 'Paused'

  const overall = status.totalItems > 0
    ? Math.round(((status.completedItems + status.partialItems + status.skippedItems) * 100) / status.totalItems)
    : 0

  const provisioning = status.totalUsersToProvision > 0
    ? Math.round((status.provisionedUsers * 100) / status.totalUsersToProvision)
    : 0

  // Slots de migração simultânea (paralelismo é por wave; capacidade = wave atual).
  // Mostra rodando/capacidade e sinaliza quando há slot ocioso com conta aguardando —
  // assim o operador sabe que existe trabalho que não está sendo puxado sozinho.
  const currentWave = status.waves.find((w: any) => w.id === status.currentWaveId)
  const slotCapacity = currentWave?.parallelism ?? status.waves[0]?.parallelism ?? 0
  const idleSlots = status.isRunning ? Math.max(0, slotCapacity - status.runningItems) : 0
  const waitingItems = status.partialItems + status.pendingItems
  const hasIdleWork = idleSlots > 0 && waitingItems > 0

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Link to={`/projects/${projectId}`} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-3">
        <ArrowLeft className="h-4 w-4" /> Voltar para o projeto
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Execução da migração</h1>
          <p className="text-sm text-slate-600 mt-1">{project?.name}</p>
        </div>
        <div className="flex gap-2">
          {canStart && (
            <button onClick={() => setShowStartModal(true)} className="btn-primary inline-flex items-center gap-2">
              <Play className="h-4 w-4" />
              {status.planStatus === 'Paused' ? 'Retomar com novo start' : 'Iniciar execução'}
            </button>
          )}
          {canPause && (
            <button onClick={() => pauseMut.mutate()} disabled={pauseMut.isPending} className="btn-secondary inline-flex items-center gap-2">
              <Pause className="h-4 w-4" /> Pausar
            </button>
          )}
          {canResume && (
            <button onClick={() => resumeMut.mutate()} disabled={resumeMut.isPending} className="btn-primary inline-flex items-center gap-2">
              <Play className="h-4 w-4" /> Retomar
            </button>
          )}
          {canCancel && (
            <button onClick={() => { if (confirm('Cancelar execução? Dados já migrados permanecerão no destino.')) cancelMut.mutate() }} disabled={cancelMut.isPending} className="btn-danger inline-flex items-center gap-2">
              <X className="h-4 w-4" /> Cancelar
            </button>
          )}
        </div>
      </div>

      {error && <div className="card mb-4 bg-red-50 border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <div className="card">
          <div className="text-xs text-slate-600 mb-1">Status</div>
          <div className="text-xl font-semibold">{translateStatus(status.planStatus)}</div>
        </div>
        <div className="card">
          <div className="text-xs text-slate-600 mb-1">Progresso</div>
          <div className="text-xl font-semibold">{overall}%</div>
          <div className="text-xs text-slate-500">{status.completedItems + status.partialItems + status.skippedItems} / {status.totalItems}</div>
        </div>
        <div className={`card${hasIdleWork ? ' border-amber-300 bg-amber-50' : ''}`}>
          <div className="text-xs text-slate-600 mb-1">Migrações simultâneas</div>
          <div className="text-xl font-semibold">{status.runningItems}{slotCapacity > 0 ? ` / ${slotCapacity}` : ''}</div>
          {hasIdleWork ? (
            <div className="text-xs text-amber-700">{idleSlots} slot(s) ocioso(s) • {waitingItems} aguardando retry</div>
          ) : (
            <div className="text-xs text-slate-500">{slotCapacity > 0 ? `paralelismo ${slotCapacity}` : '—'}</div>
          )}
        </div>
        <IdleCard idle={status.idle} />
        <div className="card">
          <div className="text-xs text-slate-600 mb-1">Provisionados</div>
          <div className="text-xl font-semibold">{status.provisionedUsers} / {status.totalUsersToProvision}</div>
          <div className="text-xs text-slate-500">{provisioning}% • {status.failedProvisionings} falha(s)</div>
        </div>
        <div className="card">
          <div className="text-xs text-slate-600 mb-1">Onda atual</div>
          <div className="text-xl font-semibold">{status.currentWaveOrder ? `${status.currentWaveOrder}` : '—'}</div>
          <div className="text-xs text-slate-500 truncate">{status.currentWaveName || 'Aguardando'}</div>
        </div>
      </div>

      <EwsLoadCard load={status.ewsLoad} projectId={projectId!} />

      <div className="mb-4">
        <CircuitBreakerBadge projectId={projectId!} />
      </div>

      {progressData?.aggregate && progressData.aggregate.totalEstimatedItems > 0 && (
        <div className="card mb-4">
          <AggregateProgressBar
            totalTransferredItems={progressData.aggregate.totalTransferredItems}
            totalEstimatedItems={progressData.aggregate.totalEstimatedItems}
            overallPercent={progressData.aggregate.overallPercent}
            elapsedSeconds={progressData.aggregate.elapsedSeconds}
          />
        </div>
      )}

      <div className="card mb-4">
        <div className="text-sm font-semibold mb-3">Ondas</div>
        <div className="space-y-2">
          {status.waves.map((w: any) => (
            <WaveRow key={w.id} wave={w} projectId={projectId!} progressByItemId={progressByItemId} onItemClick={setDrawerItemId}
              allWaves={status.waves} onMove={(waveItemId, data) => moveMut.mutate({ waveItemId, data })} />
          ))}
        </div>
      </div>

      <OneDriveFailuresCard projectId={projectId!} />

      {showStartModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold mb-3">Iniciar execução</h2>
            <p className="text-sm text-slate-600 mb-4">
              A senha provisória será aplicada a todos os usuários novos criados no destino.
              Recomenda-se forçar troca no primeiro login.
            </p>
            <label className="label">Senha provisória</label>
            <input
              className="input"
              type="text"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
            />
            <label className="flex items-center gap-2 mt-3 text-sm">
              <input type="checkbox" checked={forceChange} onChange={e => setForceChange(e.target.checked)} />
              Forçar troca de senha no primeiro login
            </label>
            <div className="flex gap-2 mt-5 justify-end">
              <button className="btn-secondary text-sm" onClick={() => setShowStartModal(false)}>Cancelar</button>
              <button
                className="btn-primary text-sm"
                disabled={password.length < 8 || startMut.isPending}
                onClick={() => startMut.mutate({ provisionalPassword: password, forcePasswordChange: forceChange })}
              >
                {startMut.isPending ? 'Iniciando...' : 'Iniciar agora'}
              </button>
            </div>
          </div>
        </div>
      )}

      <MailboxDetailDrawer
        projectId={projectId!}
        waveItemId={drawerItemId}
        progress={drawerItemId ? progressByItemId.get(drawerItemId) : undefined}
        onClose={() => setDrawerItemId(null)}
      />
    </div>
  )
}

function WaveRow({ wave, projectId, progressByItemId, onItemClick, allWaves, onMove }: { wave: any; projectId: string; progressByItemId: Map<string, MailboxProgress>; onItemClick: (id: string) => void; allWaves: any[]; onMove: (waveItemId: string, data: { targetWaveId?: string; createNewWave?: boolean }) => void }) {
  const [expanded, setExpanded] = useState(false)

  const qc = useQueryClient()
  const { data: items } = useQuery({
    queryKey: ['wave-items', wave.id],
    queryFn: () => execution.waveItems(projectId, wave.id),
    enabled: expanded,
    refetchInterval: expanded ? 3000 : false,
  })

  const parMut = useMutation({
    mutationFn: (p: number) => execution.setWaveParallelism(projectId, wave.id, p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['execution-status', projectId] }),
  })

  const pct = wave.totalItems > 0
    ? Math.round(((wave.completedItems + wave.partialItems) * 100) / wave.totalItems)
    : 0

  // UX: contas que pedem atenção no topo — em andamento E incompletas/com erro; fila no meio;
  // concluídas limpas embaixo. Ordena pelo status DERIVADO (olha as tarefas — pega o caso do wave_item
  // marcado Completed mas com tarefa falha → "Incompleto"). Sort estável preserva a ordem na faixa.
  const statusRank = (it: WaveItemDetail): number => {
    const s = deriveAccountStatus(it.status, progressByItemId.get(it.id)?.tasks ?? [])
    if (s === 'Pending' || s === 'Paused') return 1
    if (s === 'Completed' || s === 'Skipped' || s === 'Cancelled') return 2
    return 0 // Running, Incompleto, Failed, PartialSuccess, Throttled, WaitingEwsPermission, EwsPermissionTimeout
  }
  const sortedItems = items ? [...items].sort((a, b) => statusRank(a) - statusRank(b)) : []

  return (
    <div className="border border-slate-200 rounded-lg">
      <div
        role="button"
        tabIndex={0}
        className="w-full p-3 flex items-center gap-3 hover:bg-slate-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="text-sm font-mono text-slate-500 w-8">#{wave.order}</div>
        <div className="flex-1 text-left">
          <div className="text-sm font-medium">{wave.name}</div>
          <div className="text-xs text-slate-500">{wave.totalItems} usuários</div>
        </div>
        <ParallelismEditor
          value={wave.parallelism}
          pending={parMut.isPending}
          onChange={(p) => parMut.mutate(p)}
        />
        <div className="text-xs">
          <WaveStatusBadge status={wave.status} />
        </div>
        <div className="text-xs text-slate-600 w-12 text-right">{pct}%</div>
      </div>
      {expanded && items && (
        <div className="border-t border-slate-200 divide-y divide-slate-100">
          {items.length === 0 && <div className="p-3 text-xs text-slate-500">Nenhum item nesta onda</div>}
          {sortedItems.map((it: WaveItemDetail) => (
            <WaveItemRow
              key={it.id}
              item={it}
              progress={progressByItemId.get(it.id)}
              onClick={() => onItemClick(it.id)}
              currentWaveId={wave.id}
              allWaves={allWaves}
              onMove={onMove}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Sprint 9: faixa de 4 ícones (email · contatos · agenda · onedrive) colorida pelo status de cada
// tarefa, pra o operador ver de relance o que falta sem abrir a conta.
const TASK_ICONS: { type: number; label: string; Icon: typeof Mail }[] = [
  { type: 3, label: 'Email', Icon: Mail },
  { type: 6, label: 'Contatos', Icon: Users },
  { type: 5, label: 'Agenda', Icon: Calendar },
  { type: 4, label: 'OneDrive', Icon: Cloud },
  { type: 8, label: 'Tarefas', Icon: CheckSquare },
]

function taskStatusInfo(status: number | undefined): { color: string; label: string } {
  switch (status) {
    case 3: return { color: 'text-green-600', label: 'concluído' }   // verde = finalizado
    case 2: return { color: 'text-amber-500', label: 'executando' }  // laranja = executando
    case 4: return { color: 'text-red-600', label: 'erro crítico' }  // vermelho = erro
    case 5: return { color: 'text-slate-300', label: 'pulado' }      // cinza = parado
    default: return { color: 'text-slate-300', label: 'parado' }     // pendente/ausente = cinza
  }
}

function TaskIconStrip({ progress }: { progress?: MailboxProgress }) {
  const tasks = progress?.tasks ?? []
  // Sprint 9: só mostra ícone dos recursos EM ESCOPO (que têm tarefa). Recurso não-selecionado não
  // tem tarefa → sem ícone.
  const present = TASK_ICONS.filter(ti => tasks.some(t => t.taskType === ti.type))
  if (present.length === 0) return <div className="w-4 shrink-0" />
  return (
    <div className="flex items-center gap-1 shrink-0">
      {present.map(({ type, label, Icon }) => {
        const t = tasks.find(x => x.taskType === type)
        const { color, label: stLabel } = taskStatusInfo(t?.status)
        const running = t?.status === 2
        return (
          <span key={type} title={`${label}: ${stLabel}`} className="inline-flex">
            <Icon className={`h-4 w-4 ${color} ${running ? 'animate-pulse' : ''}`} />
          </span>
        )
      })}
    </div>
  )
}

function fmtBytes(b: number): string {
  if (b >= 1024 ** 3) return (b / 1024 ** 3).toFixed(1) + 'GB'
  if (b >= 1024 ** 2) return Math.round(b / 1024 ** 2) + 'MB'
  if (b >= 1024) return Math.round(b / 1024) + 'KB'
  return b + 'B'
}

// Sprint 9: anel de progresso circular (pizza/rosca) em volta do ícone do recurso. O arco preenche conforme
// migra (pct), na cor do status; o ícone fica no centro. Track cinza-claro.
function IconProgressRing({ Icon, pct, color, running }: { Icon: typeof Mail; pct: number; color: string; running: boolean }) {
  const size = 20, stroke = 2, r = (size - stroke) / 2, c = 2 * Math.PI * r
  const dash = Math.max(0, Math.min(1, pct)) * c
  return (
    <span className="relative inline-flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="absolute inset-0 -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} stroke="currentColor" className="text-slate-200" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} strokeLinecap="round" stroke="currentColor"
          strokeDasharray={`${dash} ${c}`} className={color} />
      </svg>
      <Icon className={`h-3 w-3 ${color} ${running ? 'animate-pulse' : ''}`} />
    </span>
  )
}

// Sprint 9: faixa compacta de ícone + contador por recurso (email · onedrive · contatos · agenda), lado a
// lado numa linha só. Substitui a barra que mostrava só o email — agora mostra o andamento de cada recurso.
// OneDrive mostra também bytes: "98/103 (2.4GB/3.2GB)".
function TaskCounterStrip({ progress }: { progress?: MailboxProgress }) {
  const tasks = progress?.tasks ?? []
  const present = TASK_ICONS.filter(ti => tasks.some(t => t.taskType === ti.type))
  if (present.length === 0) return <div className="h-4" />
  return (
    <div className="flex items-center gap-4 text-xs">
      {present.map(({ type, label, Icon }) => {
        const t = tasks.find(x => x.taskType === type)
        const { color, label: stLabel } = taskStatusInfo(t?.status)
        const running = t?.status === 2
        const count = t?.transferredItems ?? 0
        // migrado / total (origem). total = max(estimado, migrado) — o migrado é monotônico (nunca regride),
        // então clampar o total >= migrado evita "1500/1200".
        // Email (tipo 3) NÃO grava estimated_items por task → sem fallback mostraria migrado/migrado (parece
        // 100%). Cai no total da CONTA (estimatedItems, que tem o MailboxItemCount do discovery) descontando os
        // outros recursos que têm estimativa própria (OneDrive/contatos/calendário).
        const emailFallback = (progress?.estimatedItems ?? 0)
          - tasks.filter(x => x.taskType !== 3).reduce((s, x) => s + (x.estimatedItems ?? 0), 0)
        const baseEstimate = (type === 3 && !t?.estimatedItems) ? emailFallback : (t?.estimatedItems ?? 0)
        const total = Math.max(baseEstimate, count)
        // 0/0 = recurso sem nada a migrar → oculta o ícone pra não poluir a linha.
        if (count === 0 && total === 0) return null
        // OneDrive: bytes ao lado. PORÉM a task de OneDrive não grava estimated_bytes → sem isso o eb colapsa
        // no transferido e mostraria "126MB/126MB" (falso-100%). Então: anel/denominador por ARQUIVOS
        // (count/total, que É conhecido); bytes só como info do transferido. Quando o total de bytes existir
        // (fix backend futuro: SetEstimatedBytes), volta a mostrar o par migrado/total e o anel por bytes.
        const tb = t?.transferredBytes ?? 0
        const ebKnown = (t?.estimatedBytes ?? 0) > 0
        const eb = Math.max(t?.estimatedBytes ?? 0, tb)
        const showBytesPair = type === 4 && ebKnown
        const showBytesOnly = type === 4 && !ebKnown && tb > 0
        const pct = showBytesPair ? (eb > 0 ? tb / eb : 0) : (total > 0 ? count / total : 0)
        return (
          <span key={type} title={`${label}: ${count.toLocaleString('pt-BR')} de ${total.toLocaleString('pt-BR')}${showBytesPair ? ` (${fmtBytes(tb)} de ${fmtBytes(eb)})` : showBytesOnly ? ` (${fmtBytes(tb)} enviados)` : ''} (${stLabel})`} className="inline-flex items-center gap-1">
            <IconProgressRing Icon={Icon} pct={pct} color={color} running={running} />
            <span className="tabular-nums text-slate-600">
              {count.toLocaleString('pt-BR')}{total > 0 && <span className="text-slate-400">/{total.toLocaleString('pt-BR')}</span>}
              {showBytesPair && <span className="text-slate-400"> ({fmtBytes(tb)}/{fmtBytes(eb)})</span>}
              {showBytesOnly && <span className="text-slate-400"> ({fmtBytes(tb)})</span>}
            </span>
          </span>
        )
      })}
    </div>
  )
}

function WaveItemRow({ item, progress, onClick, currentWaveId, allWaves, onMove }: { item: WaveItemDetail; progress?: MailboxProgress; onClick: () => void; currentWaveId: string; allWaves: any[]; onMove: (waveItemId: string, data: { targetWaveId?: string; createNewWave?: boolean }) => void }) {
  // Item em execução não pode mover (backend também rejeita). Demais ondas como alvo + "Nova onda".
  const isRunning = item.status === 'Running'
  const otherWaves = allWaves.filter(w => w.id !== currentWaveId)
  const [menuOpen, setMenuOpen] = useState(false)
  const pick = (data: { targetWaveId?: string; createNewWave?: boolean }) => { setMenuOpen(false); onMove(item.id, data) }
  return (
    <div className="w-full p-3 text-sm hover:bg-slate-50 transition-colors">
      <div className="flex items-center gap-3 mb-2">
        <button type="button" onClick={onClick} className="flex items-center gap-3 flex-1 min-w-0 text-left">
          <TaskIconStrip progress={progress} />
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{item.sourceUpn}</div>
            <div className="text-xs text-slate-500 truncate">
              → {item.destinationUpn || '(sem destino)'}
              {item.provisionedAt ? ' • provisionado' : ''}
              {item.provisioningError ? ` • erro: ${item.provisioningError.substring(0, 60)}` : ''}
            </div>
          </div>
        </button>
        <ItemStatusBadge status={displayAccountStatus(item, progress)} />
        {(progress?.givenUpFailures ?? 0) > 0 && (
          <span title="mensagens não migradas (faltante desistida / quarentena) — caixa não é espelho fiel"
                className="text-[11px] text-orange-700 font-medium shrink-0 whitespace-nowrap">
            {progress!.givenUpFailures.toLocaleString('pt-BR')} não migrada(s)
          </span>
        )}
        <LiveProgressBadge item={item} />
        {/* Sprint 9: ícone de mover → popover discreto com as ondas (organização) */}
        <div className="relative shrink-0">
          <button
            type="button"
            disabled={isRunning}
            title={isRunning ? 'Item em execução — pause ou aguarde pra mover' : 'Mover para outra onda'}
            onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o) }}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          >
            <ArrowRightLeft className="h-4 w-4" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 mt-1 z-50 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1 overflow-hidden">
                <div className="px-3 py-1.5 text-xs font-medium text-slate-400 border-b border-slate-100">Mover para…</div>
                <div className="max-h-64 overflow-y-auto">
                  {otherWaves.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Sem outras ondas</div>}
                  {otherWaves.map(w => (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => pick({ targetWaveId: w.id })}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <span className="text-slate-400 font-mono text-xs w-7">#{w.order}</span>
                      <span className="truncate text-slate-700">{w.name}</span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => pick({ createNewWave: true })}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-emerald-700 border-t border-slate-100 flex items-center gap-2"
                >
                  <Plus className="h-3.5 w-3.5 shrink-0" /> Nova onda
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="pl-8">
        <TaskCounterStrip progress={progress} />
      </div>
    </div>
  )
}

// Medidor de carga EWS — "quanto a Microsoft está pensando" na passada. Verde (aliviado) → vermelho (pesado),
// pela nota de saturação (fração da janela de 2min gasta em backoff que a MS nos impôs). Mostra também o
// paralelismo REAL em curso: contas migrando × mensagens simultâneas.
function EwsLoadCard({ load, projectId }: { load?: EwsLoad; projectId: string }) {
  const qc = useQueryClient()
  const msgMut = useMutation({
    mutationFn: (p: number) => execution.setMessageParallelism(projectId, p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['execution-status', projectId] }),
  })
  if (!load) return null
  const lvl = load.level
  const theme = lvl === 'heavy'
    ? { box: 'border-red-300 bg-red-50', bar: 'bg-red-500', text: 'text-red-700', label: 'Pesado' }
    : lvl === 'moderate'
    ? { box: 'border-amber-300 bg-amber-50', bar: 'bg-amber-500', text: 'text-amber-700', label: 'Moderado' }
    : { box: 'border-green-300 bg-green-50', bar: 'bg-green-600', text: 'text-green-700', label: 'Aliviado' }
  const pct = Math.max(2, Math.min(100, Math.round(load.score)))
  return (
    <div className={`card mb-4 border ${theme.box}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold">Carga do EWS (Microsoft)</div>
        <div className={`text-xs font-semibold ${theme.text}`}>{theme.label} · {load.score}%</div>
      </div>
      <div className="h-3 w-full rounded-full bg-slate-200 overflow-hidden mb-2">
        <div className={`h-full ${theme.bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-600">
        <span><b className="text-slate-800">{load.concurrentAccounts}</b> contas migrando</span>
        <span><b className="text-slate-800">{load.concurrentMessages}</b> mensagens simultâneas (agora)</span>
        <span className={theme.text}>{load.backoffSeconds}s de backoff · {load.throttleEvents} throttles em {load.windowSeconds}s</span>
        <span className="flex items-center gap-1 ml-auto" title="Mensagens simultâneas POR CONTA (paralelismo por-mensagem). Baixar alivia o EWS — pega na próxima passada de cada conta.">
          <span className="text-slate-500">msgs/conta:</span>
          <button type="button" className="w-6 h-6 flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40"
            disabled={msgMut.isPending || load.messageParallelism <= 1} onClick={() => msgMut.mutate(load.messageParallelism - 1)}>−</button>
          <b className="w-5 text-center text-slate-800 tabular-nums">{load.messageParallelism}</b>
          <button type="button" className="w-6 h-6 flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40"
            disabled={msgMut.isPending || load.messageParallelism >= 20} onClick={() => msgMut.mutate(load.messageParallelism + 1)}>+</button>
        </span>
      </div>
    </div>
  )
}

// Termômetro de OCIOSIDADE pro gerente: tempo desde a última cópia REAL. Honesto/fase-consciente — a
// severidade (cor) vem do servidor: só vermelho quando a fase é Transferindo e mesmo assim nada copia há
// muito tempo (problema real). Em Indexando/Validando/Espelhando o tempo sobe de propósito e fica neutro.
function fmtIdle(s?: number | null): string {
  if (s == null) return '—'
  if (s < 60) return `${s} seg`
  if (s < 3600) return `${Math.floor(s / 60)} min ${s % 60} seg`
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}min`
}

function IdleCard({ idle }: { idle?: IdleMeter }) {
  // Célula compacta na grade dos cards principais. Cor vem da severidade do servidor (honesta/fase-consciente):
  // vermelho só quando deveria copiar (Transferindo) e está parado há muito; neutro em Indexando/Validando/etc.
  const active = idle?.active ?? false
  const sev = idle?.severity ?? 'ok'
  const box = sev === 'alarm' ? ' border-red-300 bg-red-50' : sev === 'warn' ? ' border-amber-300 bg-amber-50' : ''
  const txt = sev === 'alarm' ? 'text-red-700' : sev === 'warn' ? 'text-amber-700' : ''
  const title = sev === 'alarm' ? 'Deveria estar copiando e não está — verifique throttle/trava na origem'
    : idle?.context === 'Indexando' ? 'Indexando a origem — ociosidade de cópia é esperada aqui'
    : 'Tempo desde a última cópia real entre as contas em execução'
  return (
    <div className={`card${box}`} title={title}>
      <div className="text-xs text-slate-600 mb-1">Ociosidade</div>
      <div className={`text-xl font-semibold ${txt}`}>{active ? fmtIdle(idle?.seconds) : '—'}</div>
      <div className="text-xs text-slate-500 truncate">{active ? (idle?.context ?? '') : 'Nada rodando'}</div>
    </div>
  )
}

// Editor do paralelismo POR-CONTA da onda (contas em paralelo), ajustável AO VIVO. Baixar pausa conta
// automaticamente; subir inicia. stopPropagation evita expandir/recolher a onda ao clicar.
function ParallelismEditor({ value, pending, onChange }: { value: number; pending: boolean; onChange: (p: number) => void }) {
  const stop = (e: React.MouseEvent) => e.stopPropagation()
  const btn = 'w-6 h-6 flex items-center justify-center rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed'
  return (
    <div className="flex items-center gap-1" onClick={stop} title="Paralelismo por conta — contas migrando em paralelo. Ajuste ao vivo: baixar pausa uma conta, subir inicia.">
      <span className="text-slate-400 text-xs select-none">⚙</span>
      <button type="button" className={btn} disabled={pending || value <= 1} onClick={() => onChange(value - 1)}>−</button>
      <span className="w-12 text-center text-xs tabular-nums text-slate-700">
        <b>{value}</b> conta{value > 1 ? 's' : ''}
      </span>
      <button type="button" className={btn} disabled={pending || value >= 50} onClick={() => onChange(value + 1)}>+</button>
    </div>
  )
}

// Relatório geral: arquivos do OneDrive que NÃO migraram (nome + extensão + motivo). Permanente = bloqueio de
// segurança/conteúdo da Microsoft (não adianta re-rodar); transitório = token/rede (re-rodar tende a resolver).
function OneDriveFailuresCard({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false)
  const { data } = useQuery({
    queryKey: ['onedrive-failures', projectId],
    queryFn: () => migrationReport.oneDriveFailures(projectId),
    refetchInterval: 20000,
  })
  if (!data || data.total === 0) return null
  const fmtSize = (b: number) => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`
  return (
    <div className="card mb-4">
      <div role="button" tabIndex={0} className="flex items-center justify-between cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="text-sm font-semibold">Arquivos OneDrive não migrados ({data.total})</div>
        <div className="text-xs">
          <span className="text-red-700 font-semibold">{data.permanent} permanente(s)</span>
          {data.transient_ > 0 && <span className="text-amber-700 ml-2">{data.transient_} transitório(s)</span>}
          <span className="text-slate-400 ml-2">{open ? '▲' : '▼'}</span>
        </div>
      </div>
      {open && (
        <div className="mt-3">
          <div className="flex flex-wrap gap-1 mb-3">
            {data.by_extension.map((e) => (
              <span key={e.extension} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                .{e.extension}: {e.total}
              </span>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500 text-left border-b border-slate-200">
                <tr><th className="py-1 pr-2">Conta</th><th className="py-1 pr-2">Arquivo</th><th className="py-1 pr-2">Ext</th><th className="py-1 pr-2">Tam.</th><th className="py-1 pr-2">Motivo</th><th className="py-1">Tipo</th></tr>
              </thead>
              <tbody>
                {data.files.map((f, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1 pr-2 text-slate-500 truncate max-w-[120px]" title={f.source_upn}>{f.source_upn.split('@')[0]}</td>
                    <td className="py-1 pr-2 truncate max-w-[280px]" title={f.path}>{f.name}</td>
                    <td className="py-1 pr-2 font-mono text-slate-500">{f.extension ?? '—'}</td>
                    <td className="py-1 pr-2 text-slate-500 whitespace-nowrap">{fmtSize(f.size_bytes)}</td>
                    <td className="py-1 pr-2 text-slate-600">{f.reason}{f.http_status > 0 ? ` (${f.http_status})` : ''}</td>
                    <td className="py-1">
                      <span className={`px-1.5 py-0.5 rounded-full ${f.permanent ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {f.permanent ? 'Permanente' : 'Transitório'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function WaveStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    Pending: 'bg-slate-100 text-slate-700',
    Running: 'bg-amber-100 text-amber-800',
    Provisioning: 'bg-purple-100 text-purple-800',
    Paused: 'bg-blue-100 text-blue-800',
    Completed: 'bg-green-100 text-green-800',
    CompletedWithFailures: 'bg-amber-100 text-amber-800',
    Failed: 'bg-red-100 text-red-700',
    Cancelled: 'bg-slate-200 text-slate-700',
    Skipped: 'bg-slate-100 text-slate-600',
  }
  const cls = colors[status] || 'bg-slate-100'
  return <span className={`px-2 py-0.5 rounded-full text-xs ${cls}`}>{translateStatus(status)}</span>
}

// SINAL ÚNICO DE PROGRESSO: exibe o `progressSummary` derivado no SERVIDOR (verdade da cópia real, stall-consciente),
// em vez de inferir fase de task.phase (que ficava stale e gerava o fantasma "transferindo" em conta parada).
//
// Ocultar em conta terminal era a premissa errada (fix 30/08): o espelho roda EXATAMENTE sobre caixa já
// fechada, então "terminal" é o estado normal de quem está sendo espelhado. Com o badge escondido, o
// servidor dizia "Espelhando" e a tela mostrava só o chip verde "Concluído". Agora: enquanto o servidor
// reportar operação em curso (activePhase), o badge aparece — inclusive (e principalmente) em terminal.
function LiveProgressBadge({ item }: { item: WaveItemDetail }) {
  const s = item.progressSummary
  if (!s) return null
  const working = !!item.activePhase
  const terminal = ['Completed', 'PartialSuccess', 'Failed', 'Skipped', 'Cancelled', 'Paused'].includes(item.status)
  if (terminal && !working) return null
  const stalled = s.startsWith('Sem progresso') || s.includes('sem avanço')
  const copying = s.startsWith('Copiando')
  const cls = stalled ? 'bg-red-50 text-red-700 border-red-200'
    : copying ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-sky-50 text-sky-700 border-sky-200'
  return <span className={`px-2 py-0.5 rounded-full text-[11px] border ${cls}`}>{s}</span>
}

function ItemStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    Pending: 'bg-slate-100 text-slate-600',
    Running: 'bg-amber-100 text-amber-800',
    Throttled: 'bg-amber-200 text-amber-900',
    Paused: 'bg-blue-100 text-blue-800',
    Completed: 'bg-green-100 text-green-800',
    PartialSuccess: 'bg-orange-100 text-orange-800',
    Incompleto: 'bg-orange-100 text-orange-800',
    Retentando: 'bg-sky-100 text-sky-700',
    Failed: 'bg-red-100 text-red-700',
    Skipped: 'bg-slate-100 text-slate-500',
    WaitingEwsPermission: 'bg-purple-100 text-purple-800',
    EwsPermissionTimeout: 'bg-orange-100 text-orange-800',
  }
  const cls = colors[status] || 'bg-slate-100'
  return <span className={`px-2 py-0.5 rounded-full text-[11px] ${cls}`}>{translateStatus(status)}</span>
}

// Sprint 9: o status mostrado deriva das tarefas — "Concluído" (verde) só quando TODAS as tarefas em
// escopo concluíram. Qualquer tarefa incompleta/falha → "Incompleto". Estados especiais do item
// (Pausado/Pulado/permissão/provisionando/cancelado) passam direto.
function deriveAccountStatus(itemStatus: string, tasks: Array<{ status: number; updatedAt?: string | null }>): string {
  const passthrough = ['Skipped', 'Paused', 'Provisioning', 'WaitingEwsPermission', 'EwsPermissionTimeout', 'Cancelled']
  if (passthrough.includes(itemStatus)) return itemStatus
  if (tasks.length === 0) return itemStatus
  if (tasks.every(t => t.status === 3)) return 'Completed'
  // "Executando" SÓ se a task está Running E foi tocada nos últimos 15min. Task Running-mas-congelada
  // (órfã de restart, ou parada por pausa) NÃO está executando — mostrá-la "Executando" mente (caso
  // administrativo: status 2 mas parado há 40min). Vivacidade vem do updated_at, não do status enum.
  const now = Date.now()
  const live = tasks.some(t => t.status === 2 && t.updatedAt
    && (now - new Date(t.updatedAt).getTime()) < 15 * 60 * 1000)
  if (live) return 'Running'
  return 'Incompleto'
}

// Calma a UX: se a conta aparece "Incompleto" mas as pendências são TODAS transitórias
// (throttle/token/timeout/rede — re-tentadas automaticamente na próxima passada) e não há nenhuma
// de atenção (rejeição permanente/quarentena), mostra "Re-tentando" em vez de assustar com Incompleto.
function displayAccountStatus(item: WaveItemDetail, progress?: MailboxProgress): string {
  // OPERAÇÃO EM CURSO GANHA DE TODA DERIVAÇÃO (fix 30/08). O servidor é quem sabe se há trabalho rodando
  // na caixa — inclusive trabalho que roda DEPOIS dela fechar (espelho, delete). Enquanto houver fase
  // ativa, a conta não é "Concluído": era assim que a natan.moreno aparecia verde com o espelho rodando
  // nela. Quando a operação termina o servidor limpa a fase e a conta volta ao seu status real.
  if (item.activePhase) return 'Running'
  const tasks = progress?.tasks ?? []
  // Backend cravou terminal honesto não-Concluído (PartialSuccess = rodada concluída com faltante desistida;
  // Failed) → respeita, NÃO deriva "Concluído" por cima das tasks. Era o buraco: caixa com mensagens
  // desistidas lia "Concluído" porque a task de email estava status 3 (a quarentena é por-mensagem).
  if (item.status === 'PartialSuccess' || item.status === 'Failed') return item.status
  const givenUp = progress?.givenUpFailures ?? 0
  const noPending = (progress?.attentionFailures ?? 0) === 0 && (progress?.transientFailures ?? 0) === 0
  const complete = (progress?.percentItems ?? 0) >= 100
  // task efetivamente feita: Concluído(3), Pulado/Cancelado(5), ou email Failed-resíduo reconciliado 100%.
  const taskDone = (t: { taskType: number; status: number }) =>
    t.status === 3 || t.status === 5
    || (t.taskType === 3 && t.status === 4 && noPending && complete)
  // Concluído exige ZERO desistidas/quarentena — senão é espelho não-fiel → não pinta verde.
  if (tasks.length > 0 && tasks.every(taskDone) && givenUp === 0) return 'Completed'
  if (givenUp > 0) return 'PartialSuccess'

  const s = deriveAccountStatus(item.status, tasks)
  if (s === 'Incompleto'
      && (progress?.attentionFailures ?? 0) === 0
      && (progress?.transientFailures ?? 0) > 0) {
    return 'Retentando'
  }
  return s
}

function ItemStatusIcon({ status }: { status: string }) {
  if (status === 'Running' || status === 'Throttled') return <Loader2 className="h-4 w-4 text-amber-600 animate-spin" />
  if (status === 'WaitingEwsPermission') return <Loader2 className="h-4 w-4 text-purple-600 animate-spin" />
  if (status === 'EwsPermissionTimeout') return <Clock className="h-4 w-4 text-orange-600" />
  if (status === 'Completed') return <CheckCircle2 className="h-4 w-4 text-green-600" />
  if (status === 'PartialSuccess') return <AlertTriangle className="h-4 w-4 text-amber-600" />
  if (status === 'Failed') return <X className="h-4 w-4 text-red-600" />
  return <Clock className="h-4 w-4 text-slate-400" />
}

function translateStatus(s: string): string {
  const map: Record<string, string> = {
    Draft: 'Rascunho',
    Approved: 'Aprovado',
    Executing: 'Executando',
    Paused: 'Pausado',
    Completed: 'Concluído',
    Cancelled: 'Cancelado',
    Pending: 'Aguardando',
    Running: 'Executando',
    Throttled: 'Limite (esperando)',
    Provisioning: 'Provisionando',
    PartialSuccess: 'Rodada concluída',
    CompletedWithFailures: 'Concluído c/ falhas',
    WaitingEwsPermission: 'Aguardando permissão EWS',
    EwsPermissionTimeout: 'Timeout permissão EWS',
    Failed: 'Falhou',
    Skipped: 'Pulado',
    Incompleto: 'Incompleto',
    Retentando: 'Re-tentando (limite temp.)',
  }
  return map[s] || s
}
